import { useState } from "react";
import { Link } from "react-router-dom";
import { Brain, ArrowRight, Sparkles, Clock, Shuffle } from "lucide-react";
import { SiteHeader } from "@/components/Brand";
import { SiteFooter } from "@/components/SiteFooter";
import { trackFreeTool, trackFreeToolToProduct } from "@/lib/analytics";

const DEADLINE_WORDS = ["today", "due", "deadline", "asap", "overdue", "tonight", "by ", "before ", "call", "email", "reply", "send", "pay", "bill", "appointment", "pick up", "submit", "rsvp", "register", "book"];
const DEEP_WORDS = ["finish", "write", "draft", "plan", "project", "report", "design", "build", "study", "prepare", "review", "proposal", "presentation", "taxes", "application"];

function scoreTask(t) {
  const low = t.toLowerCase();
  return {
    text: t.trim(),
    deadline: DEADLINE_WORDS.some((w) => low.includes(w)),
    deep: DEEP_WORDS.some((w) => low.includes(w)),
    words: low.split(/\s+/).filter(Boolean).length,
  };
}

// Split on new lines, commas and bullet markers. Cap input at 600 chars.
export function parseTasks(raw) {
  return (raw || "")
    .slice(0, 600)
    .split(/[\n,\u2022]+/)
    .map((s) => s.replace(/^[\s\-*\u2022]+/, "").trim())
    .filter(Boolean);
}

// Deterministic and fully client side. Same inputs always give the same next
// step. `excluded` lets the user ask for a different pick.
export function computeNext(lines, energy, minutes, excluded = []) {
  const items = lines.map(scoreTask).filter((i) => i.text && !excluded.includes(i.text));
  if (items.length === 0) return null;
  let pick;
  let reason;
  const deadlineItem = items.find((i) => i.deadline);
  if (deadlineItem) {
    pick = deadlineItem;
    reason = "It looks time sensitive, so getting it moving now protects the rest of your day.";
  } else if (energy === "low") {
    pick = [...items].sort((a, b) => a.words - b.words)[0];
    reason = "Your energy is low, so the smallest task is the kindest way to build a little momentum.";
  } else if (energy === "high") {
    pick = items.find((i) => i.deep) || [...items].sort((a, b) => b.words - a.words)[0];
    reason = "You have good energy, so this is a strong moment to start the task that needs the most focus.";
  } else {
    pick = items[0];
    reason = "With steady energy, starting at the top of your list keeps the decision simple.";
  }
  const t = Math.min(Number(minutes) || 15, 25); // cap first step at 25 minutes
  const firstStep = `Set a ${t} minute timer and do only the first visible piece of this. When it ends, take a short break before deciding what is next.`;
  const rest = items.filter((i) => i.text !== pick.text).slice(0, 3).map((i) => i.text);
  return { pick: pick.text, reason, firstStep, rest };
}

const ENERGY = [
  { key: "low", label: "Low" },
  { key: "medium", label: "Some" },
  { key: "high", label: "Good" },
];
const TIMES = ["5", "15", "30", "60"];

export default function WhatToDoNext() {
  const [tasks, setTasks] = useState("");
  const [energy, setEnergy] = useState("medium");
  const [minutes, setMinutes] = useState("15");
  const [result, setResult] = useState(null);
  const [excluded, setExcluded] = useState([]);
  const [touched, setTouched] = useState(false);
  const [note, setNote] = useState("");

  const run = () => {
    setTouched(true);
    setNote("");
    const lines = parseTasks(tasks);
    const r = computeNext(lines, energy, minutes, []);
    setExcluded([]);
    setResult(r);
    // Analytics carry only energy and minutes, never the typed text.
    if (r) trackFreeTool({ energy, minutes: Number(minutes) });
  };

  const different = () => {
    const lines = parseTasks(tasks);
    const nextExcluded = result ? [...excluded, result.pick] : excluded;
    const r = computeNext(lines, energy, minutes, nextExcluded);
    if (!r) {
      setNote("That is the whole list for now. Start with the one above, or try another list.");
      return;
    }
    setNote("");
    setExcluded(nextExcluded);
    setResult(r);
  };

  const reset = () => {
    setResult(null);
    setExcluded([]);
    setTouched(false);
    setNote("");
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#0B132B]" data-testid="page-what-to-do-next">
      <SiteHeader />
      <main className="flex-1 px-6 py-14">
        <div className="mx-auto max-w-[720px]">
          <div className="flex items-center gap-2 text-[#F5A623]">
            <Sparkles className="h-5 w-5" aria-hidden="true" />
            <span className="font-heading text-xs font-bold uppercase tracking-[0.14em]">Free tool</span>
          </div>
          <h1 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-white md:text-4xl">
            What should I do next?
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-[#BFD4E8]">
            What should I do next is a free tool for ADHD overwhelm that turns a messy task list into one clear
            next step. When everything feels equally urgent, it picks one thing to start. Dump what is on your
            mind, tell it how much energy and time you have, and it will hand you a single next step.
          </p>
          <p className="mt-2 text-[13px] text-[#8FA3BC]" data-testid="wtdn-privacy">
            This runs entirely in your browser. We don't save what you type.
          </p>

          {!result && (
            <div className="mt-8 space-y-6" data-testid="wtdn-form">
              <div>
                <label htmlFor="wtdn-tasks" className="font-heading text-sm font-bold text-white">
                  Everything on your mind
                </label>
                <p className="mt-1 text-[13px] text-[#8FA3BC]">One thing per line or separated by commas. Do not tidy it up.</p>
                <textarea
                  id="wtdn-tasks"
                  data-testid="wtdn-tasks"
                  value={tasks}
                  onChange={(e) => setTasks(e.target.value)}
                  rows={6}
                  maxLength={600}
                  placeholder={"Reply to the landlord\nDraft the project update\nBook the dentist\nBuy a birthday gift"}
                  className="mt-2 w-full resize-y rounded-xl border border-white/15 bg-[#0F1B33] px-4 py-3 text-[15px] leading-relaxed text-white outline-none placeholder:text-[#5C6E86] focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/25"
                />
              </div>

              <div>
                <span className="font-heading text-sm font-bold text-white">How much energy do you have?</span>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {ENERGY.map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => setEnergy(o.key)}
                      data-testid={`wtdn-energy-${o.key}`}
                      className={`rounded-xl border px-3 py-2.5 font-heading text-sm font-bold transition-colors ${
                        energy === o.key ? "border-[#F5A623] bg-[#F5A623]/15 text-white" : "border-white/15 bg-[#0F1B33] text-[#BFD4E8] hover:border-[#F5A623]/50"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <span className="font-heading text-sm font-bold text-white">How many minutes do you have right now?</span>
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {TIMES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setMinutes(t)}
                      data-testid={`wtdn-time-${t}`}
                      className={`rounded-xl border px-3 py-2.5 font-heading text-sm font-bold transition-colors ${
                        minutes === t ? "border-[#F5A623] bg-[#F5A623]/15 text-white" : "border-white/15 bg-[#0F1B33] text-[#BFD4E8] hover:border-[#F5A623]/50"
                      }`}
                    >
                      {t === "60" ? "60+" : t}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={run}
                data-testid="wtdn-submit"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#F5A623] px-6 py-3.5 font-heading text-base font-extrabold text-[#0B132B] transition-transform duration-200 hover:-translate-y-0.5"
              >
                <Brain className="h-5 w-5" aria-hidden="true" /> Show me the one thing
              </button>
              {touched && !result && (
                <p className="text-sm text-[#F2A5A0]" data-testid="wtdn-empty">Add at least one thing above and try again.</p>
              )}
            </div>
          )}

          {result && (
            <div className="mt-8 space-y-6" data-testid="wtdn-result">
              <div className="rounded-2xl border border-[#F5A623]/40 bg-[#13233f] p-6">
                <p className="font-heading text-xs font-bold uppercase tracking-[0.14em] text-[#F5A623]">Do this next</p>
                <p className="mt-2 font-heading text-2xl font-extrabold leading-snug text-white" data-testid="wtdn-pick">{result.pick}</p>
                <p className="mt-3 text-[15px] leading-relaxed text-[#BFD4E8]">{result.reason}</p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-[#0F1B33] p-5">
                <p className="flex items-center gap-2 font-heading text-sm font-bold text-white">
                  <Clock className="h-4 w-4 text-[#3A7CA5]" aria-hidden="true" /> Your first step
                </p>
                <p className="mt-2 text-[15px] leading-relaxed text-[#BFD4E8]" data-testid="wtdn-first-step">{result.firstStep}</p>
              </div>

              {result.rest.length > 0 && (
                <div className="rounded-2xl border border-white/10 bg-[#0F1B33] p-5" data-testid="wtdn-rest">
                  <p className="font-heading text-sm font-bold text-[#8FA3BC]">Let the rest wait</p>
                  <ul className="mt-2 space-y-1 text-[15px] text-[#8FA3BC]">
                    {result.rest.map((t, i) => (<li key={i}>{t}</li>))}
                  </ul>
                </div>
              )}

              <p className="text-[15px] font-semibold text-[#BFD4E8]" data-testid="wtdn-closing">Everything else can wait for now.</p>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={different}
                  data-testid="wtdn-different"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-transparent px-4 py-2.5 font-heading text-sm font-bold text-white transition-colors hover:bg-white/5"
                >
                  <Shuffle className="h-4 w-4" aria-hidden="true" /> Pick a different one
                </button>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="wtdn-again"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-transparent px-4 py-2.5 font-heading text-sm font-bold text-white transition-colors hover:bg-white/5"
                >
                  Try another list
                </button>
              </div>
              {note && <p className="text-sm text-[#BFD4E8]" data-testid="wtdn-note">{note}</p>}

              <div className="rounded-2xl border border-white/10 bg-[#13233f] p-6" data-testid="wtdn-cta-card">
                <p className="font-heading text-lg font-extrabold text-white">Want help organizing the rest?</p>
                <Link
                  to="/get-started"
                  data-cta-location="free_tool"
                  data-testid="wtdn-product-cta"
                  onClick={() => trackFreeToolToProduct({ energy, minutes: Number(minutes) })}
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-[#F5A623] px-6 py-3.5 font-heading text-base font-extrabold text-[#0B132B] transition-transform duration-200 hover:-translate-y-0.5"
                >
                  Get CommandCenterADHD for $17, one time
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
